import { Badge, Card, Col, Row, Button, OverlayTrigger, Tooltip, Form } from "react-bootstrap";
import DropdownItem from 'react-bootstrap/DropdownItem';
import Table from "@enerex/unified-ui-react/Table";
import { titleConvert } from "./../../../helpers/api/utils";
import { trimInvalidDate, trimInvalidUTCDate } from './../../../helpers/api/utils';
import Div from "../../../components/Div";
import PageTitle from "../../../components/PageTitle/index";
import CaseHistoryDetails from "./CasehistoryDetails/index";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DEV_URL, rpost } from "../../../utils/requests";
import BlurSpan from "../../../components/BlurSpan";
import FlagIcon from 'components/icons/FlagIcon';
import ThreeDotMenu from '../../../components/ThreeDotMenu';
import ElectricityIcon from '@enerex/unified-ui-react/icons/ElectricityIcon';
import GasIcon from '@enerex/unified-ui-react/icons/GasIcon';
import ChevronDownIcon from '@enerex/unified-ui-react/icons/ChevronDownIcon';
import ChevronRightIcon from '@enerex/unified-ui-react/icons/ChevronRightIcon';
import { useAuth } from "providers/CustomAuthProvider";
import useShowSnackbar from "hooks/useShowSnackbar";
import TransferLicenseModal from "./modals/TransferLicenseModal";
import { transferLicense } from "../api/license";
import Select from "components/UnifiedSelect";
import useGlobalPremiumFlag from "hooks/useGlobalPremiumFlag";

const emptyLicenseFilters = {
  commodity: "0",
  state: "",
  activeStatus: "",
};

const CASE_HISTORY_ROW_TYPE = "case-history";

const isCaseHistoryRow = (row) => row?.__rowType === CASE_HISTORY_ROW_TYPE;

const caseHistoryExpandedRowStyles = `
  .license-case-history-row > td.license-case-history-cell {
    padding: 0 !important;
    border-top: 0;
  }

  .license-case-history-row > td.license-case-history-hidden-cell {
    display: none;
  }
`;

const getCommodityValue = (commodity) => {
  const c = String(commodity || "").trim().toLowerCase();
  if (c === "electric" || c === "electricity" || c === "1") return "1";
  if (c === "gas" || c === "2") return "2";
  return "";
};

const getActiveStatusLabel = (value) => {
  if (value === true || String(value).toLowerCase() === "true") return "Active";
  if (value === false || String(value).toLowerCase() === "false") return "Inactive";
  return "Unknown";
};

const LicenseFilters = ({ hide, globalFilter, setGlobalFilter, filterOptions }) => {
  const filters = { ...emptyLicenseFilters, ...globalFilter };
  const [formFilters, setFormFilters] = useState(filters);
  const activeStatusOptions = [
    { value: "", label: "All" },
    ...filterOptions.activeStatuses.map((status) => ({
      value: status,
      label: status,
    })),
  ];
  const stateOptions = [
    { value: "", label: "All" },
    ...filterOptions.states.map((state) => ({
      value: state,
      label: state,
    })),
  ];

  useEffect(() => {
    setFormFilters({ ...emptyLicenseFilters, ...globalFilter });
  }, [globalFilter]);

  const updateFilter = (key, value) => {
    setFormFilters((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setFormFilters(emptyLicenseFilters);
    setGlobalFilter(emptyLicenseFilters);
    hide();
  };

  const applyFilters = (event) => {
    event.preventDefault();
    setGlobalFilter(formFilters);
    hide();
  };

  return (
    <Div className="p-2">
      <Form onSubmit={applyFilters}>
        <Form.Label>Commodity</Form.Label>
        <Div className="d-flex justify-content-between mb-3">
          <Form.Check
            type="radio"
            name="licenseCommodity"
            id="license-commodity-electric"
            checked={formFilters.commodity === "1"}
            onChange={() => updateFilter("commodity", "1")}
            label={
              <span className="d-inline-flex align-items-center gap-1">
                <ElectricityIcon />
                Electric
              </span>
            }
          />
          <Form.Check
            type="radio"
            name="licenseCommodity"
            id="license-commodity-gas"
            checked={formFilters.commodity === "2"}
            onChange={() => updateFilter("commodity", "2")}
            label={
              <span className="d-inline-flex align-items-center gap-1">
                <GasIcon />
                Nat Gas
              </span>
            }
          />
        </Div>
        <Div className="justify-content-center mb-3">
          <Form.Check
            type="radio"
            name="licenseCommodity"
            id="license-commodity-both"
            checked={formFilters.commodity === "0"}
            onChange={() => updateFilter("commodity", "0")}
            label={
              <span className="d-inline-flex align-items-center gap-1">
                <ElectricityIcon />
                <GasIcon />
                Both
              </span>
            }
          />
        </Div>
        <Form.Group className="mb-3">
          <Form.Label>License Status</Form.Label>
          <Select
            value={activeStatusOptions.find((option) => option.value === formFilters.activeStatus) || null}
            options={activeStatusOptions}
            onChange={(option) => updateFilter("activeStatus", option?.value || "")}
            isSearchable={false}
            isClearable={false}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>State</Form.Label>
          <Select
            value={stateOptions.find((option) => option.value === formFilters.state) || null}
            options={stateOptions}
            onChange={(option) => updateFilter("state", option?.value || "")}
            isSearchable={false}
            isClearable={false}
          />
        </Form.Group>
        <Div>
          <Button type="button" variant="primary" onClick={resetFilters}>
            Reset
          </Button>
          <Button className="float-end" type="submit" variant="primary">
            Apply
          </Button>
        </Div>
      </Form>
    </Div>
  );
};

const LicenseAppliedFilters = ({ globalFilters, setGlobalFilters }) => {
  const filters = { ...emptyLicenseFilters, ...globalFilters };
  const badges = [];

  if (filters.commodity === "1") {
    badges.push({ key: "commodity", label: "COMMODITY", value: "Electric", clearValue: "0" });
  } else if (filters.commodity === "2") {
    badges.push({ key: "commodity", label: "COMMODITY", value: "Nat Gas", clearValue: "0" });
  }

  if (filters.activeStatus) {
    badges.push({
      key: "activeStatus",
      label: "LICENSE STATUS",
      value: filters.activeStatus,
      clearValue: "",
    });
  }

  if (filters.state) {
    badges.push({ key: "state", label: "STATE", value: filters.state, clearValue: "" });
  }

  if (badges.length === 0) {
    return null;
  }

  return (
    <Div className="d-flex mb-2 flex-wrap">
      {badges.map((badge) => (
        <Badge className="mx-1 mb-1" bg="secondary" key={badge.key}>
          {badge.label}: {badge.value}{" "}
          <i
            className="mdi mdi-close"
            onClick={() =>
              setGlobalFilters({ ...filters, [badge.key]: badge.clearValue })
            }
            role="button"
          ></i>
        </Badge>
      ))}
    </Div>
  );
};

const Casehistories = ({ licenseId, companyId }) => {
  const [caseHistoryData, setCaseHistoryData] = useState();
  const [loadingCaseHistoryData, setloadingCaseHistoryData] = useState();

  useEffect(() => {
    setloadingCaseHistoryData(true);

    const fetchCaseHistory = async () => {
      const caseHistoryDataResponse = await rpost(
        `${DEV_URL}/api/licenseCaseHistory/getall`,
        {
          PageNumber: 1,
          PageSize: 50,
          SearchTerm: "",
          companyId: companyId,
          licenseId: licenseId
        }
      );

      setCaseHistoryData(caseHistoryDataResponse);
      setloadingCaseHistoryData(false);
    };

    fetchCaseHistory();

    return () => {
      setCaseHistoryData();
    };
  }, [companyId, licenseId]);

  return (
    <>
      {caseHistoryData?.data?.licenseCaseHistory && (
        <CaseHistoryDetails
          loadingCaseHistoryData={loadingCaseHistoryData}
          caseHistoryData={caseHistoryData.data.licenseCaseHistory}
        />
      )}
    </>
  );
};

const CaseHistoryExpandedCell = ({ licenseId, companyId, colSpan }) => {
  const contentRef = useRef(null);

  useLayoutEffect(() => {
    const cell = contentRef.current?.closest("td");
    const row = cell?.parentElement;

    if (!cell || !row) {
      return undefined;
    }

    const cells = Array.from(row.children);
    cell.colSpan = colSpan;
    cell.classList.add("license-case-history-cell");
    cells.slice(1).forEach((siblingCell) => {
      siblingCell.classList.add("license-case-history-hidden-cell");
    });

    return () => {
      cell.colSpan = 1;
      cell.classList.remove("license-case-history-cell");
      cells.slice(1).forEach((siblingCell) => {
        siblingCell.classList.remove("license-case-history-hidden-cell");
      });
    };
  }, [colSpan]);

  return (
    <div ref={contentRef} className="w-100">
      <Casehistories licenseId={licenseId} companyId={companyId} />
    </div>
  );
};

// var ShowModal = (url) => { };
export const LicenseAndCaseHistories = ({ companyId, fetchedData, subscribed, onScreenshotClick }) => {
  const { isSystemAdmin } = useAuth();
  const isPremium = useGlobalPremiumFlag();
  const snackbar = useShowSnackbar();
  const debounceTimerRef = useRef(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [selectedTargetCompany, setSelectedTargetCompany] = useState(null);
  const [transferReason, setTransferReason] = useState('');
  const [transferError, setTransferError] = useState('');
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [licenseFilters, setLicenseFilters] = useState(emptyLicenseFilters);

  const closeTransferModal = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    setShowTransferModal(false);
    setSelectedLicense(null);
    setSelectedTargetCompany(null);
    setTransferReason('');
    setTransferError('');
    setTransferSubmitting(false);
  };

  const openTransferModal = (row) => {
    setSelectedLicense(row);
    setSelectedTargetCompany(null);
    setTransferReason('');
    setTransferError('');
    setShowTransferModal(true);
  };

  const loadTransferCompanyOptions = useCallback(async (inputValue) => {
    if (!inputValue) {
      return [];
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    return new Promise((resolve) => {
      debounceTimerRef.current = setTimeout(async () => {
        try {
          const response = await rpost(`${DEV_URL}/api/company/SearchCompany`, {
            Search: inputValue,
            Limit: 50,
          });

          if (response?.data?.data) {
            const options = response.data.data
              .filter((c) => c.id !== companyId)
              .map((c) => ({ value: c, label: c.legalName }));
            resolve(options);
            return;
          }
          resolve([]);
        } catch (error) {
          resolve([]);
        }
      }, 300);
    });
  }, [companyId]);

  const onTransferConfirm = async () => {
    if (!selectedLicense?.licenseId) {
      setTransferError('Invalid license selected');
      return;
    }
    if (!selectedTargetCompany?.value?.id) {
      setTransferError('Please select target company');
      return;
    }
    if (!transferReason || transferReason.trim() === '') {
      setTransferError('Reason is required');
      return;
    }

    try {
      setTransferSubmitting(true);
      setTransferError('');

      const response = await transferLicense({
        licenseId: selectedLicense.licenseId,
        targetCompanyId: selectedTargetCompany.value.id,
        reason: transferReason.trim(),
      });

      if (response?.data?.success) {
        snackbar('License transferred successfully', 'success');
        closeTransferModal();
        await getLicenseData();
      } else {
        setTransferError(response?.data?.message || 'Failed to transfer license');
      }
    } catch (error) {
      setTransferError('An error occurred while transferring license');
    } finally {
      setTransferSubmitting(false);
    }
  };

  const licenseColumns = [
    {
      id: "caseHistoryExpander",
      header: "",
      cell: ({ row }) => {
        const original = row.original;

        if (isCaseHistoryRow(original)) {
          return (
            <CaseHistoryExpandedCell
              licenseId={original.licenseId}
              companyId={companyId}
              colSpan={licenseColumns.length}
            />
          );
        }

        if (!row.getCanExpand()) {
          return null;
        }

        return (
          <Button
            type="button"
            variant="link"
            size="sm"
            className="p-0 text-primary"
            aria-label={
              row.getIsExpanded()
                ? "Collapse case histories"
                : "Expand case histories"
            }
            onClick={row.getToggleExpandedHandler()}
          >
            {row.getIsExpanded() ? (
              <ChevronDownIcon fontSize={16} />
            ) : (
              <ChevronRightIcon fontSize={16} />
            )}
          </Button>
        );
      },
      enableSorting: false,
      enableHiding: false,
    },
    {
      id: "commodity",
      header: "",
      accessorFn: (row) => getCommodityValue(row.commodity),
      cell: ({ row }) =>
        isCaseHistoryRow(row.original)
          ? null
          : commodityIcon(row.original.commodity ?? ''),
      enableSorting: false,
      enableHiding: false,
    },
    {
      header: "State",
      accessorKey: "stateCode",
    },
    {
      header: "Legal Name",
      accessorFn: (row) => titleConvert(row.associatedLegalName ?? ''),
      cell: ({ row }) => (
        isCaseHistoryRow(row.original) ? null : (
          <span className="ellipsis" title={row.original.associatedLegalName}>
            {row.original.associatedLegalName}
          </span>
        )
      ),
    },
    {
      header: "License Number",
      accessorKey: "licenseNumber",
      cell: ({ row: tableRow }) => {
        const row = tableRow.original;
        if (isCaseHistoryRow(row)) return null;

        const isActive = row.licenseStatusIsActive;
        const normalizedStatus = row.normalisedLicenseStatus;
        const dotColor = normalizedStatus?.toLowerCase() === "pending withdrawal" ? "#ffc107" : isActive === true ? "#28a745" : "#dc3545";
        const licenseContent = (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              cursor: isPremium ? "help" : "default"
            }}
          >
            {
              isActive != null ? <span
                style={{
                  width: "8px",
                  height: "8px",
                  borderRadius: "50%",
                  backgroundColor: dotColor,
                  display: "inline-block",
                  flexShrink: 0
                }} /> : <span
                style={{
                  width: "8px",
                  height: "8px",
                }}
              />
            }
            <span>{row.licenseNumber}</span>
          </span>
        );

        return (
          <BlurSpan isBlur={!isPremium}>
            {isPremium && isActive != null && isActive !== undefined && normalizedStatus ? (
              <OverlayTrigger
                placement="bottom"
                delay={{ show: 250, hide: 100 }}
                overlay={
                  <Tooltip id={`license-status-tooltip-${row.id || row.licenseId || Math.random()}`}>
                    {normalizedStatus}
                  </Tooltip>
                }
              >
                {licenseContent}
              </OverlayTrigger>
            ) : (
              licenseContent
            )}
          </BlurSpan>
        );
      },
    },
    {
      header: "Start Date",
      accessorFn: (row) => {
        if (isCaseHistoryRow(row)) return null;
        const d = new Date(row.licenseRegisteredDate);
        return isNaN(d) ? null : d?.getTime();
      },
      cell: ({ row: tableRow }) => {
        const row = tableRow.original;
        if (isCaseHistoryRow(row)) return null;

        if (!isPremium) {
          return <BlurSpan isBlur={true}>XXXXXX</BlurSpan>;
        }
        return <BlurSpan isBlur={!isPremium}>{trimInvalidUTCDate(row.licenseRegisteredDate?.split("T")[0])}</BlurSpan>;
      },
    },
    {
      header: "End Date",
      accessorFn: (row) => {
        if (isCaseHistoryRow(row)) return null;
        const d = new Date(row.licenseExpiryDate);
        return isNaN(d) ? null : d?.getTime();
      },
      cell: ({ row: tableRow }) => {
        const row = tableRow.original;
        if (isCaseHistoryRow(row)) return null;

        if (!isPremium) {
          return <BlurSpan isBlur={true}>XXXXXX</BlurSpan>;
        }
        return <BlurSpan isBlur={!isPremium}>{trimInvalidUTCDate(row.licenseExpiryDate?.split("T")[0])}</BlurSpan>;
      },
    },
    {
      header: "License Type",
      accessorKey: "licenseType",
    },

    // {
    //     name: "Last Updated On",
    //     selector: (row) => trimInvalidDate(row?.lastUpdatedOn?.split("T")[0]) || trimInvalidDate(row?.createdDate?.split("T")[0]),
    //     // grow: 1,
    //     sortable: false,
    // },
    {
      header: "Added to Datahub",
      accessorFn: (row) => trimInvalidDate(row?.createdDate?.split("T")[0]) || trimInvalidDate(row?.createdDate?.split("T")[0]),
      enableSorting: false,
    },

    {
      header: "Last Found On",
      accessorFn: (row) => trimInvalidUTCDate(row?.lastFoundOn?.split("T")[0]) || trimInvalidUTCDate(row?.createdDate?.split("T")[0]),
      cell: ({ row: tableRow }) => {
        const row = tableRow.original;
        if (isCaseHistoryRow(row)) return null;

        const lastFoundDate = trimInvalidUTCDate(row?.lastFoundOn?.split("T")[0]) || trimInvalidUTCDate(row?.createdDate?.split("T")[0]);
        const isLatest = row?.isLatest || false;
        return <span style={!isLatest ? { color: 'red' } : {}}>{lastFoundDate}</span>;
      },
      enableSorting: false,
    },
    {
      header: "Source File Date",
      accessorFn: (row) => trimInvalidUTCDate(row?.sourceFileDate ?? ''),
    },
    {
      id: 'actions',
      header: 'Action',
      cell: ({ row: tableRow }) => {
        const row = tableRow.original;
        if (isCaseHistoryRow(row)) return null;

        var sourceBtn;
        var screenshotBtn;
        // Single tooltip used for all disabled options
        const tooltip = (
          <Tooltip
            id={`license-actions-tooltip-${row.id || row.licenseId || Math.random()}`}
            style={{ pointerEvents: "auto", zIndex: 2000 }}
          >
            Upgrade your plan to access this functionality. Click{" "}
            <a
              href="https://enerex.com/company/contact-us/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#fff", textDecoration: "underline" }}
              onClick={(e) => e.stopPropagation()}
            >
              here
            </a>{" "}
            to contact sales.
          </Tooltip>
        );

        // Wrap a menu option so tooltip shows when hovering over that option (for non-premium)
        const withTooltipIfNotPremium = (child) =>
          isPremium ? (
            child
          ) : (
            <OverlayTrigger
              placement="left"
              trigger={['hover', 'focus']}
              delay={{ show: 600, hide: 1000 }}       // show delay prevents tooltip when quickly moving through options, hide delay gives time to click link
              overlay={tooltip}
            >
              <span>{child}</span>
            </OverlayTrigger>
          );

        if (row.sourceUrl !== null && row.sourceUrl !== undefined && row.sourceUrl !== '') {
          const baseSource = (
            <DropdownItem
              disabled={!isPremium}
              onClick={(e) => {
                const a = document.createElement('a');
                a.href = row.sourceUrl;
                a.target = '_blank';
                a.click();
              }}
              style={{ cursor: !isPremium ? 'not-allowed' : 'pointer' }}
            >
              <small>Source Site</small>
            </DropdownItem>
          );
          sourceBtn = withTooltipIfNotPremium(baseSource);
        }
        if (row.screenshotUrl !== null && row.screenshotUrl !== undefined && row.screenshotUrl !== '') {
          const baseScreenshot = (
            <DropdownItem
              disabled={!isPremium}
              onClick={() => {
                onScreenshotClick(
                  (row.screenshotUrl === null || row.screenshotUrl === undefined)
                    ? ''
                    : row.screenshotUrl
                );
              }}
              style={{ cursor: !isPremium ? 'not-allowed' : 'pointer' }}
            >
              <small>Screenshot</small>
            </DropdownItem>
          );
          screenshotBtn = withTooltipIfNotPremium(baseScreenshot);
        }
        const menu = (
          <ThreeDotMenu className="text-center">
            {screenshotBtn}
            {sourceBtn}
            {withTooltipIfNotPremium(
              <DropdownItem
                disabled={!isPremium}
                onClick={(e) => {
                  onFollowClick(
                    companyId,
                    row.stateCode,
                    row.commodity,
                    row.licenseType,
                    row.isSubscribe
                  );
                }}
                style={{ cursor: !isPremium ? 'not-allowed' : 'pointer' }}
              >
                <small>{row.isSubscribe ? 'Unfollow' : 'Follow'}</small>
              </DropdownItem>
            )}
            {isSystemAdmin && (
              <DropdownItem
                onClick={() => openTransferModal(row)}
              >
                <small>Transfer</small>
              </DropdownItem>
            )}
          </ThreeDotMenu>
        );

        return <div className="d-flex">
          {menu}
          {row.isSubscribe && <FlagIcon className='light-green ms-1' />}
        </div>
      },

      enableSorting: false,
      enableHiding: false,
    },
  ];

  const commodityIcon = (commodity) => {
    const c = (commodity || "").toLowerCase();
    if (c === "electric" || c === "electricity" || c === "1") {
      return <ElectricityIcon style={{ width: 18, height: 18 }} />;
    }
    if (c === "gas" || c === "2") {
      return <GasIcon style={{ width: 18, height: 18 }} />;
    }
    return "-";
  };


  const [companyLicenseData, setCompanyLicenseData] = useState();
  const [loadingCompanyDetailsData, setLoadingCompanyDetailsData] = useState();
  const licenseRows = useMemo(
    () => (Array.isArray(companyLicenseData) ? companyLicenseData : []),
    [companyLicenseData]
  );
  const licenseFilterOptions = useMemo(() => {
    const buildOptions = (values) =>
      Array.from(new Set(values.filter(Boolean))).sort((a, b) =>
        String(a).localeCompare(String(b), undefined, { sensitivity: "base" })
      );

    return {
      states: buildOptions(
        licenseRows.map((license) => String(license.stateCode || "").trim())
      ),
      activeStatuses: buildOptions(
        licenseRows.map((license) =>
          getActiveStatusLabel(license.licenseStatusIsActive)
        )
      ),
    };
  }, [licenseRows]);

  const filteredLicenseData = useMemo(() => {
    return licenseRows
      .filter((license) => {
        const commodity = getCommodityValue(license.commodity);
        const state = String(license.stateCode || "").trim();
        const activeStatus = getActiveStatusLabel(license.licenseStatusIsActive);

        return (
          (licenseFilters.commodity === "0" || commodity === licenseFilters.commodity) &&
          (!licenseFilters.state || state === licenseFilters.state) &&
          (!licenseFilters.activeStatus ||
            activeStatus === licenseFilters.activeStatus)
        );
      })
      .map((license) => ({
        ...license,
        caseHistoryRows: license.licenseId
          ? [
              {
                id: `${license.id}-case-history`,
                __rowType: CASE_HISTORY_ROW_TYPE,
                licenseId: license.licenseId,
              },
            ]
          : [],
      }));
  }, [licenseRows, licenseFilters]);

  const getLicenseData = async () => {

    setLoadingCompanyDetailsData(true);
    const companyLicensesDataResponse = await rpost(
      `${DEV_URL}/api/license/getall`,
      {
        PageNumber: 1,
        PageSize: 250,
        SearchTerm: "",
        companyId: companyId,
      }
    );

    // TODO: for now craeting id for each license, need to work on better approach to handle duplicate license ids
    const companyLicenseList = companyLicensesDataResponse?.data?.license?.map((license, index) => {
      return { ...license, id: index, licenseId: license?.id }
    });
    setLoadingCompanyDetailsData(false);
    //case History

    setCompanyLicenseData(companyLicenseList ?? []);
  }
  const followCompany = async (companyId, isSubscribed) => {

    const response = await rpost(`${DEV_URL}/api/Company/SubscribeNotification`, {
      CompanyId: companyId, IsSubscribe: !isSubscribed
    });

    if (response !== "Data not found") {
      if (response.data.includes('Notifications')) {
        subscribed[1](!isSubscribed);
        //setIsSubscribe(!isSubscribed);
      }
    }
  }
  const onFollowClick = async (companyId, stateCode, commodity, licenseType, isSubscribed) => {
    const response = await rpost(`${DEV_URL}/api/Company/SubscribeNotification`, {
      CompanyId: companyId, IsSubscribe: !isSubscribed, StateCode: stateCode, Commodity: commodity, LicenseType: licenseType
    });

    if (response !== "Data not found") {
      if (response.data.includes('Notifications')) {
        await getLicenseData();
        subscribed[1](!isSubscribed);
      }
    }
  }
  // ShowModal = fetchedData.screenShotEvent;
  // useEffect(async ()=>{
  //   await getLicenseData();
  // }, [onCompanyFollowChange]);


  useEffect(() => {
    console.log(subscribed);
    const fetch = async () => {
      await getLicenseData();
    };
    fetch();
    return () => {
      setCompanyLicenseData();
    };
  }, [subscribed[0]]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
  }, []);

  return (
    <span>
      <style>{caseHistoryExpandedRowStyles}</style>
      <Div className="d-flex align-items-center justify-content-between">
        <PageTitle title={"Licenses"} />
        <Div className="d-flex align-items-center gap-1">
          <Button
            variant={subscribed[0] ? "outline-primary" : "primary"}
            size="sm"
            className="fs-10"
            disabled={!isPremium}
            onClick={() => {
              if (!isPremium) return;
              followCompany(companyId, subscribed[0]);
            }}
            style={{ cursor: !isPremium ? 'not-allowed' : 'pointer' }}
          >
            <small>
              <FlagIcon className={`${subscribed[0] ? "" : ""} me-1`} />{" "}
              {subscribed[0] ? "Unfollow" : "Follow"}
            </small>
          </Button>
        </Div>
      </Div>
      <Card className="">
        <Card.Body>
          <Row>
            <Col sm="12">
              {companyLicenseData === "Data not found" && <p>No data found</p>}
              {companyLicenseData !== "Data not found" && (
                <Table
                  columns={licenseColumns}
                  data={filteredLicenseData}
                  loading={loadingCompanyDetailsData}
                  enableSearch={false}
                  enablePagination={false}
                  enableHideColumns={false}
                  enableSorting
                  enableExpanding
                  enableFilters={licenseRows.length > 0}
                  enableSelection={false}
                  enablePaginateAll={false}
                  enableColumnVisibilityStorage={false}
                  enableGlobalFilterStorage={false}
                  enableUrlPagination={false}
                  globalFilter={licenseFilters}
                  onGlobalFilterChange={setLicenseFilters}
                  filterComponent={(props) => (
                    <LicenseFilters
                      {...props}
                      filterOptions={licenseFilterOptions}
                    />
                  )}
                  appliedFilters={LicenseAppliedFilters}
                  getSubRows={(row) => row.caseHistoryRows}
                  rowClassName={(row) =>
                    isCaseHistoryRow(row.original)
                      ? "license-case-history-row"
                      : ""
                  }
                  noDataMessage="No data found"
                />
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>
      <TransferLicenseModal
        show={showTransferModal}
        onClose={closeTransferModal}
        currentCompanyName={selectedLicense?.associatedLegalName || fetchedData?.company?.legalName || ''}
        selectedLicense={selectedLicense}
        selectedTargetCompany={selectedTargetCompany}
        onTargetCompanyChange={setSelectedTargetCompany}
        transferReason={transferReason}
        onReasonChange={setTransferReason}
        loadCompanyOptions={loadTransferCompanyOptions}
        onConfirmTransfer={onTransferConfirm}
        transferError={transferError}
        isSubmitting={transferSubmitting}
      />
    </span>
  );
};
